import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faFilter,
  faXmark,
  faChevronLeft,
  faChevronRight,
  faArrowUpRightFromSquare,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const STATUS_LABELS = {
  PREVIOUSLY_MARRIED: "Previously Married",
  WIDOWED: "Widowed",
  AWAITING_DIVORCE: "Awaiting Divorce",
};

const STATUS_COLORS = {
  PREVIOUSLY_MARRIED: "bg-purple-100 text-purple-700",
  WIDOWED:            "bg-gray-100 text-gray-600",
  AWAITING_DIVORCE:   "bg-orange-100 text-orange-700",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[status] || "bg-gray-100 text-gray-600"}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const getAge = (dob) => {
  if (!dob) return "—";
  const years = Math.floor((Date.now() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
  return isNaN(years) ? "—" : `${years} yrs`;
};

/* ── Analytics sub-components ── */

const AnalyticsSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    {/* Stats row */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 h-24" />
      ))}
    </div>
    {/* Two-column row */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-gray-100 p-5 h-40" />
      <div className="bg-white rounded-xl border border-gray-100 p-5 h-40" />
    </div>
    {/* Second two-column row */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-gray-100 p-5 h-40" />
      <div className="bg-white rounded-xl border border-gray-100 p-5 h-40" />
    </div>
    {/* Connect performance */}
    <div className="bg-white rounded-xl border border-gray-100 p-5 h-32" />
  </div>
);

const StatCard = ({ label, value, color }) => (
  <div className={`bg-white rounded-xl border border-l-4 ${color} p-5`}>
    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-3xl font-bold text-gray-800">{value ?? "—"}</p>
    <p className="text-xs text-gray-400 mt-1">profiles</p>
  </div>
);

const HorizontalBar = ({ label, value, max, color, suffix }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-gray-500">
        <span>{label}</span>
        <span className="font-medium text-gray-700">{suffix !== undefined ? suffix : value}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const GenderSplit = ({ data }) => {
  const male   = data?.MALE   ?? 0;
  const female = data?.FEMALE ?? 0;
  const total  = male + female || 1;
  const mPct = Math.round((male / total) * 100);
  const fPct = Math.round((female / total) * 100);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Gender Split</p>
      <div className="space-y-3">
        <HorizontalBar label="Male" value={male} max={total} color="bg-blue-400" suffix={`${male} (${mPct}%)`} />
        <HorizontalBar label="Female" value={female} max={total} color="bg-pink-400" suffix={`${female} (${fPct}%)`} />
      </div>
    </div>
  );
};

const ProfileCompleteness = ({ data }) => {
  const hasAboutMe     = data?.hasAboutMe     ?? 0;
  const avgPhotos      = data?.avgPhotos      ?? 0;
  const hasProfileImg  = data?.hasProfileImage ?? 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Profile Completeness</p>
      <div className="space-y-3">
        <HorizontalBar label="Has About Me" value={hasAboutMe} max={100} color="bg-emerald-400" suffix={`${hasAboutMe}%`} />
        <HorizontalBar label="Avg Photos" value={avgPhotos} max={10} color="bg-amber-400" suffix={avgPhotos.toFixed(1)} />
        <HorizontalBar label="Has Profile Image" value={hasProfileImg} max={100} color="bg-violet-400" suffix={`${hasProfileImg}%`} />
      </div>
    </div>
  );
};

const AgeDistribution = ({ data }) => {
  const max = data?.length ? Math.max(...data.map(d => d.count)) : 1;
  const COLORS = ["bg-indigo-400", "bg-blue-400", "bg-cyan-400", "bg-teal-400", "bg-emerald-400"];

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Age Distribution</p>
      {(!data || data.length === 0) ? (
        <p className="text-sm text-gray-400">No data</p>
      ) : (
        <div className="space-y-2">
          {data.map((bucket, i) => (
            <HorizontalBar
              key={bucket._id}
              label={bucket._id}
              value={bucket.count}
              max={max}
              color={COLORS[i % COLORS.length]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const JoinTrend = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Monthly Join Trend</p>
        <p className="text-sm text-gray-400">No data</p>
      </div>
    );
  }

  const sorted = [...data].sort((a, b) => a._id.localeCompare(b._id));
  const maxCount = Math.max(...sorted.map(d => d.count), 1);
  const W = 280;
  const H = 80;
  const PAD = { top: 8, bottom: 20, left: 8, right: 8 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const n = sorted.length;

  const pts = sorted.map((d, i) => {
    const x = PAD.left + (n > 1 ? (i / (n - 1)) * chartW : chartW / 2);
    const y = PAD.top + chartH - (d.count / maxCount) * chartH;
    return [x, y];
  });

  const polyline = pts.map(([x, y]) => `${x},${y}`).join(" ");
  const areaPath = `M ${pts[0][0]},${PAD.top + chartH} ` +
    pts.map(([x, y]) => `L ${x},${y}`).join(" ") +
    ` L ${pts[pts.length - 1][0]},${PAD.top + chartH} Z`;

  // Show first, middle, last month labels
  const labelIdxs = n <= 3 ? sorted.map((_, i) => i) : [0, Math.floor(n / 2), n - 1];

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Monthly Join Trend</p>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 200 }}>
          <defs>
            <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#C9A84C" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {/* Area fill */}
          <path d={areaPath} fill="url(#trendGrad)" />
          {/* Line */}
          <polyline points={polyline} fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {/* Dots */}
          {pts.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3" fill="#C9A84C" />
          ))}
          {/* Month labels */}
          {labelIdxs.map((idx) => (
            <text
              key={idx}
              x={pts[idx][0]}
              y={H - 4}
              textAnchor="middle"
              fontSize="7"
              fill="#9ca3af"
            >
              {sorted[idx]._id}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
};

const ConnectPerformance = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Connect Performance</p>
        <p className="text-sm text-gray-400">No data</p>
      </div>
    );
  }

  const total    = data.reduce((acc, d) => acc + d.count, 0) || 1;
  const accepted = data.find(d => d._id === "ACCEPTED")?.count ?? 0;
  const rate     = Math.round((accepted / total) * 100);

  const STATUS_BAR_COLORS = {
    ACCEPTED:  "bg-emerald-400",
    REJECTED:  "bg-red-400",
    PENDING:   "bg-amber-400",
    WITHDRAWN: "bg-gray-400",
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Connect Performance</p>
      <div className="flex items-center gap-8">
        {/* Big acceptance rate number */}
        <div className="flex-shrink-0 text-center">
          <p className="text-4xl font-bold text-emerald-500">{rate}%</p>
          <p className="text-xs text-gray-400 mt-1">Acceptance Rate</p>
          <p className="text-xs text-gray-400">{accepted}/{total} connects</p>
        </div>
        {/* Status breakdown bars */}
        <div className="flex-1 space-y-2">
          {data.map((d) => (
            <HorizontalBar
              key={d._id}
              label={d._id.charAt(0) + d._id.slice(1).toLowerCase()}
              value={d.count}
              max={total}
              color={STATUS_BAR_COLORS[d._id] || "bg-gray-400"}
              suffix={`${d.count} (${Math.round((d.count / total) * 100)}%)`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Main component ── */

const SecondMarriagePool = () => {
  const navigate = useNavigate();

  // Profiles state
  const [profiles, setProfiles]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [total, setTotal]           = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [activeFilters, setActiveFilters] = useState({ status: "", gender: "" });
  const limit = 20;

  // Analytics state
  const [analytics, setAnalytics]           = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [showAnalytics, setShowAnalytics]   = useState(true);

  const fetchProfiles = useCallback(async (page, status, gender) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (status) params.maritalStatus = status;
      if (gender) params.gender = gender;
      const res = await func.get(urls.SECOND_MARRIAGE_POOL_URL, { params });
      if (res?.status === 200) {
        setProfiles(res.data.profiles || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch {
      toast.error("Failed to load second marriage pool");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await func.get("mst-admin/second-marriage-analytics");
      if (res?.status === 200) {
        setAnalytics(res.data);
      }
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles(1, "", "");
    fetchAnalytics();
  }, [fetchProfiles, fetchAnalytics]);

  const applyFilter = () => {
    setActiveFilters({ status: statusFilter, gender: genderFilter });
    setCurrentPage(1);
    fetchProfiles(1, statusFilter, genderFilter);
    setShowFilter(false);
  };

  const clearFilter = () => {
    setStatusFilter(""); setGenderFilter("");
    setActiveFilters({ status: "", gender: "" });
    setCurrentPage(1);
    fetchProfiles(1, "", "");
    setShowFilter(false);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
    fetchProfiles(p, activeFilters.status, activeFilters.gender);
  };

  const hasFilters = activeFilters.status || activeFilters.gender;

  // Derived analytics values
  const byStatus       = analytics?.summary?.byStatus       ?? {};
  const byGender       = analytics?.summary?.byGender       ?? {};
  const profileComp    = analytics?.profileCompleteness     ?? {};
  const ageDistribution = analytics?.ageDistribution        ?? [];
  const joinTrend      = analytics?.joinTrend               ?? [];
  const connectStats   = analytics?.connectStats            ?? [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faHeart} className="text-pink-400" />
            Second Marriage Pool
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">
            BG profiles with Previously Married, Widowed, or Awaiting Divorce status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAnalytics(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition border ${
              showAnalytics
                ? "bg-[#C9A84C] border-[#C9A84C] text-white"
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            <FontAwesomeIcon icon={faChartBar} className="text-xs" />
            Analytics
          </button>
          <button
            onClick={() => setShowFilter(v => !v)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition"
          >
            <FontAwesomeIcon icon={showFilter ? faXmark : faFilter} className="text-xs" />
            Filter
            {hasFilters && <span className="w-2 h-2 rounded-full bg-[#C9A84C]" />}
          </button>
        </div>
      </div>

      {/* Analytics section */}
      {showAnalytics && (
        <div className="space-y-4">
          {analyticsLoading ? (
            <AnalyticsSkeleton />
          ) : (
            <>
              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard
                  label="Total in Pool"
                  value={analytics?.summary?.total ?? total}
                  color="border-pink-300"
                />
                <StatCard
                  label="Previously Married"
                  value={byStatus.PREVIOUSLY_MARRIED}
                  color="border-purple-300"
                />
                <StatCard
                  label="Widowed"
                  value={byStatus.WIDOWED}
                  color="border-gray-300"
                />
                <StatCard
                  label="Awaiting Divorce"
                  value={byStatus.AWAITING_DIVORCE}
                  color="border-orange-300"
                />
              </div>

              {/* Gender + Completeness row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <GenderSplit data={byGender} />
                <ProfileCompleteness data={profileComp} />
              </div>

              {/* Age Distribution + Join Trend row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AgeDistribution data={ageDistribution} />
                <JoinTrend data={joinTrend} />
              </div>

              {/* Connect Performance — full width */}
              <ConnectPerformance data={connectStats} />
            </>
          )}
        </div>
      )}

      {/* Filter panel */}
      {showFilter && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex flex-wrap gap-4 items-end shadow-sm">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30"
            >
              <option value="">All statuses</option>
              <option value="PREVIOUSLY_MARRIED">Previously Married</option>
              <option value="WIDOWED">Widowed</option>
              <option value="AWAITING_DIVORCE">Awaiting Divorce</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
            <select
              value={genderFilter}
              onChange={e => setGenderFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30"
            >
              <option value="">All genders</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={applyFilter} className="px-4 py-2 bg-[#C9A84C] text-white rounded-lg text-sm font-medium hover:bg-[#b8953f] transition">Apply</button>
            <button onClick={clearFilter} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition">Clear</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <p className="text-sm font-semibold text-gray-700">{total} profiles</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-pink-300 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No profiles found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Gender</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Age</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">City</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Added</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {profiles.map((p) => (
                  <tr key={p._id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3.5 font-medium text-gray-800">{p.firstName || "—"}</td>
                    <td className="px-5 py-3.5 text-gray-500 capitalize">{p.gender?.toLowerCase() || "—"}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={p.maritalStatus} /></td>
                    <td className="px-5 py-3.5 text-gray-500">{getAge(p.dateOfBirth)}</td>
                    <td className="px-5 py-3.5 text-gray-500">{p.residentLocation?.city || "—"}</td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">
                      {new Date(p.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => navigate(`/user-management/bride-groom-profile/view/${p._id}`)}
                        className="text-gray-400 hover:text-[#C9A84C] transition p-1"
                        title="View profile"
                      >
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-xs" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">Page {currentPage} of {totalPages}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecondMarriagePool;
